import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { fetchPricingConfig } from '../services/pricingService';
import { computeFlatRateTotals } from '../lib/flatRateTotals';

// ── Service-specific scope options ───────────────────────────────────────────
const SCOPE_OPTIONS = {
  pest_control: [
    'Interior + Exterior',
    'Exterior Only',
    'Interior Only',
    'Crawlspace Treatment',
    'Mosquito Control',
    'Termite Inspection',
  ],
  lawn_care: [
    'Mow + Edge + Blow',
    'Full Service (Mow + Edge + Trim + Blow)',
    'Fertilization',
    'Aeration + Overseeding',
    'Weed Control',
    'Seasonal Cleanup',
  ],
};

// ── Frequency definitions ────────────────────────────────────────────────────
// Calculator no longer asks the operator to pick a duration — the Service Plan
// panel in Quote Summary owns cadence (driven by the selected Service Program,
// with manual override fallback). All we need here is a default interval so
// that `recurringPlan` can initialise with a sensible value before the
// operator picks a program. Legacy setting values (monthly/quarterly/etc.) map
// to days; 'one-time' means the service is non-recurring.
const DEFAULT_INTERVAL_FOR_TYPE = {
  'one-time':   null,
  monthly:      30,
  'bi-monthly': 60,
  quarterly:    90,
  custom:       30, // legacy 'custom' — fall back to monthly; operator can override in Service Plan panel
  recurring:    30, // forward-compatible value for the new boolean semantics
};

const DEFAULT_RECURRING_INTERVAL = 30;

// ── Band lookup helper ───────────────────────────────────────────────────────
function findBandForSqFt(sqFt, bands) {
  if (!Array.isArray(bands) || !bands.length || !sqFt || sqFt <= 0) return null;
  return (
    bands.find((band) => {
      const aboveMin = sqFt >= (band.minSqFt ?? 0);
      const belowMax = band.maxSqFt === null || sqFt <= band.maxSqFt;
      return aboveMin && belowMax;
    }) ?? null
  );
}

/**
 * FlatRateCalculatorPanel
 *
 * Shared calculator panel for flat-rate / recurring services (Pest Control, Lawn Care).
 * Does NOT call the backend pricing API — all math is client-side.
 * Output shape matches existing calculator panels so it integrates with
 * ServiceCalculatorDashboard's `calculations[serviceId]` state.
 *
 * Props:
 *   serviceId         — 'pest_control' | 'lawn_care' (used as the key in calculations)
 *   serviceName       — display name
 *   ServiceIcon       — lucide icon component
 *   isOpen            — collapsible state
 *   onToggle          — collapsible handler
 *   onServiceTotalChange — (serviceId, data) → updates parent calculations state
 */
const FlatRateCalculatorPanel = ({
  serviceId,
  serviceName,
  ServiceIcon,
  isOpen,
  onToggle,
  onServiceTotalChange,
  // P1: sole tax authority — company rate from settings/billing (0 when
  // disabled / unset). Flat-rate panels must NOT invent their own tax.
  taxRate = 0,
  // accepted for interface parity but unused:
  // eslint-disable-next-line no-unused-vars
  measurementTotals, manualOverride, setManualOverride,
}) => {
  // sq ft input — drives band lookup when bands are configured in Settings
  const [sqFt, setSqFt]                         = useState('');
  const [serviceScope, setServiceScope]         = useState('');
  const [perVisitPrice, setPerVisitPrice]       = useState('');
  const [materialCost, setMaterialCost]         = useState('');
  // Single boolean: "Sell as service program" — operator declares intent.
  // Interval + program binding are owned by the Service Plan panel downstream.
  const [isRecurring, setIsRecurring]           = useState(false);
  // Fallback interval if operator never selects a program in Quote Summary.
  // Seeded from settings.defaultFrequency mapping, editable via the Service
  // Plan panel's existing manual interval control (not via this card).
  const [defaultIntervalDays, setDefaultIntervalDays] = useState(DEFAULT_RECURRING_INTERVAL);

  // Settings-loaded defaults (contains `bands` array after our backend migration)
  const [loadedConfig, setLoadedConfig]         = useState(null);
  // Guard: once user manually types a price, band lookups stop overwriting it
  const priceUserEdited = useRef(false);

  // ── Load settings defaults on mount ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function loadDefaults() {
      try {
        const resp = await fetchPricingConfig(serviceId);
        if (cancelled || !resp?.success || !resp.config) return;
        const cfg = resp.config;
        setLoadedConfig(cfg);
        // Pre-populate material cost and frequency from settings
        // Price is NOT pre-filled here — it requires a sq ft entry to pick the right band
        if (cfg.materialCostDefault > 0) {
          setMaterialCost(String(cfg.materialCostDefault));
        }
        if (cfg.defaultFrequency) {
          const mapped = DEFAULT_INTERVAL_FOR_TYPE[cfg.defaultFrequency];
          // Pre-check the "Sell as service program" box when settings default
          // to any non-one-time cadence. Interval defaults from the same map.
          if (cfg.defaultFrequency !== 'one-time') {
            setIsRecurring(true);
            if (mapped) setDefaultIntervalDays(mapped);
          }
        }
      } catch {
        // No config in settings yet — panel stays blank, user fills manually
      }
    }
    loadDefaults();
    return () => { cancelled = true; };
  }, [serviceId]);

  // ── Band lookup: update price when sq ft changes (unless manually edited) ─
  useEffect(() => {
    if (!loadedConfig || priceUserEdited.current) return;
    const parsed = parseFloat(sqFt);
    if (!parsed || parsed <= 0) return;
    const band = findBandForSqFt(parsed, loadedConfig.bands);
    if (band !== null) {
      setPerVisitPrice(band.basePrice > 0 ? String(band.basePrice) : '');
    }
    // No match → leave price unchanged; user must enter manually
  }, [sqFt, loadedConfig]);

  // ── Matched band (for hint display) ──────────────────────────────────────
  const matchedBand = useMemo(() => {
    const parsed = parseFloat(sqFt);
    if (!loadedConfig?.bands?.length || !parsed || parsed <= 0) return null;
    return findBandForSqFt(parsed, loadedConfig.bands);
  }, [sqFt, loadedConfig]);

  const scopeOptions = SCOPE_OPTIONS[serviceId] || [];

  // ── Live calculation preview ─────────────────────────────────────────────
  const calc = useMemo(() => {
    const base      = parseFloat(perVisitPrice) || 0;
    const materials = parseFloat(materialCost)  || 0;
    const { subtotal, rate, tax, grandTotal } = computeFlatRateTotals(base, materials, taxRate);
    const grossMarginPct =
      base > 0 ? ((base - materials) / base * 100) : null;
    return { base, materials, subtotal, grossMarginPct, rate, tax, grandTotal };
  }, [perVisitPrice, materialCost, taxRate]);

  const derivedIntervalDays = useMemo(() => {
    return isRecurring ? defaultIntervalDays : null;
  }, [isRecurring, defaultIntervalDays]);

  const frequencyLabel = useMemo(() => {
    return isRecurring ? 'Recurring' : 'One-time';
  }, [isRecurring]);

  // ── Commit calculation to parent ─────────────────────────────────────────
  const handleCalculate = () => {
    if (!calc.base || calc.base <= 0) {
      toast.error('Please enter a per-visit price greater than $0.');
      return;
    }

    onServiceTotalChange(serviceId, {
      input: {
        propertySqFt:  parseFloat(sqFt) || null,
        serviceScope:  serviceScope || null,
        perVisitPrice: calc.base,
        materialCost:  calc.materials,
        frequency: {
          // 'recurring' | 'one-time' — downstream RoofEstimator useEffect keys
          // off `type !== 'one-time' && intervalDays` so this stays compatible.
          type:         isRecurring ? 'recurring' : 'one-time',
          intervalDays: derivedIntervalDays,
          label:        frequencyLabel,
        },
      },
      result: {
        serviceTotal: calc.subtotal,
        discount:     0,
        tax:          calc.tax,
        taxRate:      calc.rate,      // record the rate actually applied
        subtotal:     calc.subtotal,
        grandTotal:   calc.grandTotal,
      },
      measurement: null,
    });

    toast.success(`${serviceName} pricing set: $${calc.subtotal.toFixed(2)}`);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="overflow-hidden">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <ServiceIcon className="h-5 w-5 text-secondary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-secondary">{serviceName}</h3>
              <p className="text-sm text-muted-foreground">
                {loadedConfig ? 'Defaults from Settings · override if needed' : 'Set flat-rate pricing'}
              </p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-6 space-y-5 bg-muted/30">

            {/* ── Property Sq Ft ─────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor={`sqft-${serviceId}`}>
                Property Size (sq ft)
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional — drives price lookup)</span>
              </Label>
              <Input
                id={`sqft-${serviceId}`}
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 5,000"
                value={sqFt}
                onChange={(e) => setSqFt(e.target.value)}
                className="font-mono"
                data-testid={`sqft-input-${serviceId}`}
              />
              {/* Band match hint */}
              {sqFt && parseFloat(sqFt) > 0 && (
                matchedBand ? (
                  <p className="text-xs text-secondary">
                    Band matched: {matchedBand.minSqFt.toLocaleString()}–
                    {matchedBand.maxSqFt !== null
                      ? matchedBand.maxSqFt.toLocaleString()
                      : '∞'} sq ft · ${matchedBand.basePrice}
                  </p>
                ) : (
                  loadedConfig?.bands?.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      No price band defined for this size — enter price manually.
                    </p>
                  )
                )
              )}
            </div>

            {/* ── Service Scope (optional) ────────────────────────────── */}
            {scopeOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Service Scope <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                <Select
                  value={serviceScope}
                  onValueChange={setServiceScope}
                >
                  <SelectTrigger data-testid={`scope-select-${serviceId}`}>
                    <SelectValue placeholder="Select scope…" />
                  </SelectTrigger>
                  <SelectContent>
                    {scopeOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ── Pricing ─────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`pvp-${serviceId}`}>Per-visit Price *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    id={`pvp-${serviceId}`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={perVisitPrice}
                    onChange={(e) => {
                      priceUserEdited.current = true;
                      setPerVisitPrice(e.target.value);
                    }}
                    className="pl-7 font-mono"
                    data-testid={`per-visit-price-${serviceId}`}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor={`mat-${serviceId}`}>Material Cost <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    id={`mat-${serviceId}`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={materialCost}
                    onChange={(e) => setMaterialCost(e.target.value)}
                    className="pl-7 font-mono"
                    data-testid={`material-cost-${serviceId}`}
                  />
                </div>
              </div>
            </div>

            {/* ── Sell as service program ─────────────────────────────── */}
            <div className="rounded-lg border border-input bg-muted/30 p-3">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <Checkbox
                  checked={isRecurring}
                  onCheckedChange={(v) => setIsRecurring(v === true)}
                  className="mt-0.5"
                  data-testid={`recurring-toggle-${serviceId}`}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">Sell as service program</div>
                  {isRecurring ? (
                    <div
                      className="text-xs text-muted-foreground mt-1"
                      data-testid={`recurring-helper-${serviceId}`}
                    >
                      Select the program in <span className="font-medium text-foreground">Quote Summary →</span>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground mt-1">
                      One-time service — no recurring plan will be created.
                    </div>
                  )}
                </div>
              </label>
            </div>

            {/* ── Live Calculation Preview ─────────────────────────────── */}
            {calc.subtotal > 0 && (
              <div className="rounded-lg border border-secondary/20 bg-secondary/5 p-4 space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service charge</span>
                  <span className="font-mono font-medium">${calc.base.toFixed(2)}</span>
                </div>
                {calc.materials > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Materials</span>
                    <span className="font-mono font-medium">+ ${calc.materials.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-semibold border-t border-secondary/20 pt-1.5 mt-1.5">
                  <span className="text-secondary">Per-visit Total</span>
                  <span className="font-mono text-secondary">${calc.subtotal.toFixed(2)}</span>
                </div>
                {calc.grossMarginPct !== null && calc.materials > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Gross margin</span>
                    <span
                      className={
                        calc.grossMarginPct >= 50 ? 'text-green-600 font-medium' :
                        calc.grossMarginPct >= 25 ? 'text-amber-600 font-medium' :
                        'text-red-500 font-medium'
                      }
                    >
                      {calc.grossMarginPct.toFixed(1)}%
                    </span>
                  </div>
                )}
                {isRecurring && (
                  <div className="text-xs text-muted-foreground pt-1 border-t border-secondary/10">
                    Recurring service — configure plan in Quote Summary →
                  </div>
                )}
              </div>
            )}

            {/* ── Calculate Button ─────────────────────────────────────── */}
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
              onClick={handleCalculate}
              data-testid={`calculate-btn-${serviceId}`}
            >
              Set {serviceName} Pricing
            </Button>

          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default FlatRateCalculatorPanel;
